import { from } from 'rxjs'
import { catchError, concatMap, filter, map, switchMap } from 'rxjs/operators'
import { errorNotification, successNotification, ProductService } from '~/Services'
import { IPermissionImportResult } from '~/Services/Product.interface'
import { RootEpic } from '../types'
import { ProductActions } from './slice'

const getProductsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.getProducts.match),
    switchMap(() =>
      // paging: false -> backend trả về mảng thuần (ApiQuery<Product>.ListResultAsync), đúng convention
      // đã dùng ở Store/License/epics.ts, Store/ApiKey/epics.ts cho cùng endpoint này.
      from(ProductService.Get.getProducts({ params: { paging: false } })).pipe(
        map(res => ProductActions.getProductsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ProductActions.getProductsFail()]
        }),
      ),
    ),
  )

const createProductEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.createProduct.match),
    switchMap(action =>
      from(ProductService.Post.createProduct(action.payload)).pipe(
        concatMap(() => {
          successNotification('Tạo sản phẩm thành công!')
          return [
            ProductActions.createProductSuccess(),
            ProductActions.getProducts(),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Tạo sản phẩm thất bại')
          return [ProductActions.createProductFail()]
        }),
      ),
    ),
  )

const updateProductEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.updateProduct.match),
    switchMap(action => {
      const { id, data, silent } = action.payload
      return from(ProductService.Put.updateProduct(id, data)).pipe(
        concatMap(() => {
          if (!silent) {
            successNotification('Cập nhật sản phẩm thành công!')
          }
          return [
            ProductActions.updateProductSuccess(),
            ProductActions.getProducts(),
          ]
        }),
        catchError(error => {
          errorNotification(error, 'Cập nhật sản phẩm thất bại')
          return [ProductActions.updateProductFail()]
        }),
      )
    }),
  )

const importPermissionsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.importPermissions.match),
    switchMap(action => {
      const { productId, file } = action.payload
      return from(ProductService.Post.importPermissions(productId, file)).pipe(
        map(res => ProductActions.importPermissionsSuccess(res.data as IPermissionImportResult)),
        catchError(error => {
          // Workbook validation lỗi (400) vẫn trả đúng shape IPermissionImportResult (khác shape lỗi chung
          // {Message}) -> nhận diện qua "errors" là mảng, hiển thị structured result thay vì toast chung.
          const data = error?.response?.data
          if (data && Array.isArray(data.errors)) {
            return [ProductActions.importPermissionsSuccess(data as IPermissionImportResult)]
          }
          // Còn lại (404 Product not found, 401/403, 500, network error...) -> toast chung + tắt loading.
          errorNotification(error)
          return [ProductActions.importPermissionsFail()]
        }),
      )
    }),
  )

const getPermissionGroupsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.getPermissionGroups.match),
    switchMap(action =>
      from(ProductService.Get.getPermissionGroups(action.payload)).pipe(
        map(res => ProductActions.getPermissionGroupsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ProductActions.getPermissionGroupsFail()]
        }),
      ),
    ),
  )

const searchPermissionsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.searchPermissions.match),
    switchMap(action => {
      const { productId, ...params } = action.payload
      return from(ProductService.Get.searchPermissions(productId, params)).pipe(
        map(res => ProductActions.searchPermissionsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ProductActions.searchPermissionsFail()]
        }),
      )
    }),
  )

const createPermissionEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.createPermission.match),
    switchMap(action => {
      const { productId, data } = action.payload
      return from(ProductService.Post.createPermission(productId, data)).pipe(
        concatMap(() => {
          successNotification('Tạo permission thành công!')
          return [ProductActions.createPermissionSuccess()]
        }),
        catchError(error => {
          errorNotification(error, 'Tạo permission thất bại')
          return [ProductActions.createPermissionFail()]
        }),
      )
    }),
  )

const updatePermissionEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.updatePermission.match),
    switchMap(action => {
      const { productId, permissionId, data } = action.payload
      return from(ProductService.Put.updatePermission(productId, permissionId, data)).pipe(
        concatMap(() => {
          successNotification('Cập nhật permission thành công!')
          return [ProductActions.updatePermissionSuccess()]
        }),
        catchError(error => {
          errorNotification(error, 'Cập nhật permission thất bại')
          return [ProductActions.updatePermissionFail()]
        }),
      )
    }),
  )

const deletePermissionEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.deletePermission.match),
    switchMap(action => {
      const { productId, permissionId } = action.payload
      return from(ProductService.Delete.deletePermission(productId, permissionId)).pipe(
        concatMap(() => {
          successNotification('Xoá permission thành công!')
          return [ProductActions.deletePermissionSuccess()]
        }),
        catchError(error => {
          errorNotification(error, 'Xoá permission thất bại')
          return [ProductActions.deletePermissionFail()]
        }),
      )
    }),
  )

const bulkDeletePermissionsEpic: RootEpic = action$ =>
  action$.pipe(
    filter(ProductActions.bulkDeletePermissions.match),
    switchMap(action => {
      const { productId, permissionIds } = action.payload
      return from(ProductService.Delete.bulkDeletePermissions(productId, permissionIds)).pipe(
        concatMap(() => {
          successNotification(`Đã xoá ${permissionIds.length} permission!`)
          return [ProductActions.bulkDeletePermissionsSuccess()]
        }),
        catchError(error => {
          errorNotification(error, 'Xoá permission thất bại')
          return [ProductActions.bulkDeletePermissionsFail()]
        }),
      )
    }),
  )

export const ProductEpics = [
  getProductsEpic,
  createProductEpic,
  updateProductEpic,
  importPermissionsEpic,
  getPermissionGroupsEpic,
  searchPermissionsEpic,
  createPermissionEpic,
  updatePermissionEpic,
  deletePermissionEpic,
  bulkDeletePermissionsEpic,
]
